import { getLatestDraftOrSaved } from './callModContent';
import getJWT from './getJWT';
import getQueryParameter from './getQueryParameter';
import getTenant from './getTenant';
import findTopWindow from './findTopWindow';
import { get, getCourseId, getFirm } from './SCORM';
import usePreloadImages from './usePreloadImages';
import bugAlert from './bugAlert';
import { nextTick } from 'vue';
import mainCssRaw from '../main.css?inline';
import fetchContent from './fetchContent';
import getContentBase from './getContentBase';

const editorWindow = findTopWindow({
  withOpeners: true,
  predicate: w => 'getEditorDraft' in w
});

// @ts-ignore
const getEditorDraft = editorWindow?.getEditorDraft;
// @ts-ignore
// const getLatestDraftYaml = editorWindow?.getLatestDraftYaml;

const clientEditMode = getFirm() && get('clientEditMode');

interface Module<T> {
  default: T;
}

type YAML = Module<Content>;
type Globbed<T> = Record<string, () => Promise<Module<T>>>;

const firm = getFirm();
const courseId = getCourseId();
const globbedYml = import.meta.glob<YAML>('../content/*.yml');
const customGlobbedYml = import.meta.glob<YAML>('../customizations/**/*.yml');
const customPath = `../customizations/${firm}/${firm}_${courseId}.yml`;

let fetchedCustomYaml = false;

export function hasCustomYaml() {
  return fetchedCustomYaml || customPath in customGlobbedYml;
}

async function loadPath<T>(globbed: Globbed<T>, path: string) {
  if (path in globbed) {
    try {
      const module = await globbed[path]();
      if (!module?.default) return null;
      return module.default;
    } catch (e) {
      console.warn(`Failed to load YAML file at ${path}:`, e);
      return null;
    }
  }
}

function injectMainCSS(css: string) {
  if (!css) return;

  const style = document.createElement('style');
  style.innerHTML = css;
  style.id = 'main-css';
  document.head.append(style);
}

function applyCourseCss(css: string) {
  if (!css) return;
  const isntCss = css.trim().startsWith('<');
  if (isntCss) return;

  const [authored, tailwind] = css.split('/* TAILWIND */');
  const mainCssEl = document.getElementById('main-css') as HTMLStyleElement;
  const mainCssString = mainCssEl.innerHTML;

  let newCss = mainCssString;

  newCss = newCss.replace(
    '[data-after-tailwind-components]',
    tailwind + '\n[data-after-tailwind-components]'
  );

  newCss = newCss.replace(
    '[data-after-tailwind-utilities]',
    authored + '\n[data-after-tailwind-utilities]'
  );

  mainCssEl.innerHTML = newCss;
}

export async function loadCourseCss({ isWord = false } = {}) {
  const familyId =
    courseId.toString().length === 6 ? courseId.toString().slice(0, 3) : '';

  const base = getContentBase();
  const customFolder = `${base}/custom/${firm.toLowerCase()}`;
  const baseFolder = `${base}`;

  const customCssUrl1 = `${customFolder}/${firm}_${courseId}.css`;
  const customCssUrl2 = `${customFolder}/${firm.toLowerCase()}_${courseId}.css`;
  const customCssUrl3 = `${customFolder}/${firm.toUpperCase()}_${courseId}.css`;

  const baseCssUrl = `${baseFolder}/${courseId}.css`;
  const familyCssUrl = `${baseFolder}/${familyId}.css`;

  injectMainCSS(
    isWord
      ? '/* INJECT YAML CSS ABOVE */ /* INJECT AUTHORED CSS ABOVE */'
      : mainCssRaw
  );

  const [baseCss, customCss, familyCss] = await Promise.all([
    // css on S3
    fetchContent(baseCssUrl).catch(() => ''),

    firm !== '___'
      ? fetchContent(customCssUrl1)
          .catch(() => fetchContent(customCssUrl2))
          .catch(() => fetchContent(customCssUrl3))
          .catch(() => '')
      : '',

    familyId && fetchContent(familyCssUrl).catch(() => '')
  ]);

  applyCourseCss(familyCss);
  applyCourseCss(baseCss);
  applyCourseCss(customCss);
}

export function mergeCustomContent(
  content: MaybeContent,
  customContent: MaybeContent
) {
  // merge custom content
  const mergedContent = { ...content, ...customContent } as Content;

  mergedContent.course = {
    ...mergedContent?.course,
    ...mergedContent?.patches?.course
  };

  if (!mergedContent?.course?.title) {
    console.error('[mergeCustomContent] no course title — throwing', {
      courseId,
      hasContent: !!content,
      contentTitle: content?.course?.title,
      contentPages: content?.pages?.length,
      hasCustomContent: !!customContent,
      customTitle: customContent?.course?.title,
      customPatchCourse: customContent?.patches?.course
    });
    throw new Error(
      'Could not find a valid course for the given ID, ' + courseId
    );
  }

  mergedContent?.pages.forEach(p => {
    p.screens = p.screens.map(
      s => mergedContent.patches?.screens?.[s.id]?.[0] || s
    );
  });

  mergeLocalStorage(mergedContent);
  nextTick(() => checkForDupIds(mergedContent));

  return mergedContent;
}

function mergeLocalStorage(content) {
  if (courseId === 0) {
    const storageKeys = [
      'uc-color-gray',
      'uc-color-primary',
      'uc-color-secondary',
      'uc-theme'
    ];

    for (const k of storageKeys) {
      const v = localStorage[k];
      const kk = k.split('-').slice(-1)[0];

      if (v) {
        content.course[kk] = v;
      }
    }
  }

  return content;
}

const local = Boolean(getQueryParameter('local', findTopWindow()));
const internal = Boolean(getQueryParameter('internal', findTopWindow()));
const audioDoc = Boolean(getQueryParameter('audioDoc'));
const wordDoc = Boolean(getQueryParameter('word'));
const ucAuthorAudio = Boolean(getQueryParameter('ucAuthorAudio'));
const ucClientEditModeAudio = Boolean(
  getQueryParameter('ucClientEditModeAudio')
);
const previewDraft = Boolean(getQueryParameter('previewDraft'));

async function fetchJSON(
  courseId: number,
  firm?: string
): Promise<Content | null> {
  if (local || firm === '___') return null;

  // the authoring editor - we don't want draft content for audio docs, only saved content
  if (getEditorDraft && !audioDoc) {
    const draft: CourseDraft = getEditorDraft();

    const typeMatches =
      (draft?.type === 'course' && !firm) || (draft?.type !== 'course' && firm);

    if (draft && typeMatches) {
      const yaml = await import('js-yaml');
      const json = yaml.load(draft.yaml);

      return json as Content;
    }

    if (draft.compiledCss) {
      applyCourseCss(draft.compiledCss);
    }
  }

  // client portal edit mode, or
  // client portal edit mode resync audio
  if ((firm && clientEditMode) || (firm && ucClientEditModeAudio && audioDoc)) {
    try {
      const r = await getLatestDraftOrSaved({ courseId, firmCode: firm });
      if (!r) return null;

      const yaml = await import('js-yaml');
      const json = yaml.load(r.yaml);

      return json as Content;
    } catch (e) {
      // if we errored while fetching the custom content in edit mode, we need to throw an error to prevent the course from loading otherwsie they can overwrite any saved edits with a new, empty draft.
      throw new Error(
        `Failed to fetch custom content from the database for ${firm}, ${courseId}`
      );
    }
  }

  const isCustom = getFirm() !== '___';

  // creating audio from the UC Author/Lite (both base and customised courses)
  if (ucAuthorAudio && audioDoc) {
    // `firm` is set for the custom-content fetch, undefined for the base fetch.
    const slot = firm ? 'custom' : 'base';
    try {
      const courseProps = firm ? { courseId, firmCode: firm } : { courseId };
      const r = await getLatestDraftOrSaved(courseProps);

      /**
       * We may want the base or custom content loaded from the DB depending if this is a custom/bespoke course or not.
       * For customised courses, its **base** content should be taken from the published json on S3 not the DB.
       * We only want base content from the DB if the audio is for that base course itself, not for a customisation of it.
       */
      const typeMatches =
        (r?.type === 'course' && !isCustom) ||
        (r?.type !== 'course' && isCustom);

      console.log('[getContent audio] DB lookup', {
        courseId,
        firm,
        slot,
        isCustom,
        hasRecord: !!r,
        recordType: r?.type,
        typeMatches
      });

      if (r && typeMatches) {
        const yaml = await import('js-yaml');
        const json = yaml.load(r.yaml);

        console.log('[getContent audio] using DB record for', slot, {
          courseId,
          firm,
          title: (json as Content)?.course?.title
        });

        return json as Content;
      }

      // No matching DB record (e.g. a customisation's base course, which is
      // only published on S3): fall through to the S3 fetch below instead of
      // returning null, otherwise mergeCustomContent gets an empty base and
      // throws "missing title".
      console.log(
        `[getContent audio] no matching DB record for ${slot} — falling back to S3`,
        { courseId, firm, isCustom }
      );
    } catch (e) {
      // The custom-content fetch (firm set) is fatal — we must not silently
      // load an empty draft. The base fetch (no firm) for a customisation
      // should fall back to S3, so only re-throw for the custom call.
      const fatal = isCustom && Boolean(firm);
      console.warn(
        '[getContent audio] DB fetch error',
        {
          courseId,
          firm,
          slot,
          isCustom,
          fatal
        },
        e
      );
      if (fatal) {
        throw new Error(
          `Failed to fetch content from the database for ${firm}, ${courseId}`
        );
      }
    }
  }

  // preview iframe (author editor): the customization may be an unpublished
  // draft/saved that isn't on S3 yet. Prefer mod-content over S3; fall back to
  // S3 only if nothing is found or the request fails.
  if (firm && previewDraft) {
    try {
      const r = await getLatestDraftOrSaved({ courseId, firmCode: firm });
      if (r) {
        const yaml = await import('js-yaml');
        return yaml.load(r.yaml) as Content;
      }
    } catch (e) {
      console.warn(
        `Preview draft fetch failed for ${firm}, ${courseId}; falling back to S3`,
        e
      );
    }
  }

  // published on s3
  const base = getContentBase();
  const customFolder = `${base}/custom/${firm?.toLowerCase()}`;
  const baseFolder = `${base}`;

  const urls = firm
    ? [
        `${customFolder}/${firm}_${courseId}.json`,
        `${customFolder}/${firm.toLowerCase()}_${courseId}.json`,
        `${customFolder}/${firm.toUpperCase()}_${courseId}.json`
      ]
    : [`${baseFolder}/${courseId}.json`];

  let raw = '';
  let fetchedUrl = '';

  for (const url of urls) {
    raw = await fetchContent(url).catch(() => '');
    if (raw) {
      fetchedUrl = url;
      break;
    }
  }

  // if we still don't have a raw string, throw an error
  if (!raw) {
    throw new Error(
      `Could not find published JSON for ${courseId}, ${firm || 'base'}`
    );
  }

  let json: Content;
  try {
    json = JSON.parse(raw);
    // in case the JSON is a stringified JSON
    if (typeof json === 'string') json = JSON.parse(json);
  } catch (e) {
    throw new Error(
      `Could not parse JSON for ${courseId}, ${firm || 'base'} from ${fetchedUrl}: ${
        (e as Error).message
      }`
    );
  }

  if (!json) {
    throw new Error(`Could not load JSON for ${courseId}, ${firm || 'base'}`);
  }

  console.log('[getContent S3] fetch', {
    courseId,
    firm,
    slot: firm ? 'custom' : 'base',
    fetchedUrl,
    triedUrls: urls
  });

  return json;
}

// FIXME expose this in useContent
let _customContent: Partial<Content> | null | undefined = null;

export function getCustomContent() {
  return _customContent;
}

type MaybeContent = Content | null | undefined;

export default async function getContent(): Promise<Content> {
  if (typeof courseId !== 'number') {
    throw new Error('No valid course ID.');
  }

  const path = `../content/${courseId}.yml`;
  const customPath = `../customizations/${firm}/${firm}_${courseId}.yml`;

  // load content
  let content: MaybeContent = await loadPath<Content>(globbedYml, path);
  let customContent: MaybeContent = await loadPath<Content>(
    customGlobbedYml,
    customPath
  );

  const jwt = clientEditMode ? getJWT() : null;
  const validJwt = jwt && jwt.exp * 1000 > Date.now();

  try {
    const fetched = await fetchJSON(courseId);
    if (fetched) content = fetched;
  } catch (e) {
    console.warn(e);
  }

  if (clientEditMode && !validJwt) {
    throw new Error(`No token found for ${getTenant()}.`);
  }

  try {
    const fetched = await fetchJSON(courseId, firm);
    if (fetched) customContent = fetched;
    if (fetched) fetchedCustomYaml = true;
  } catch (e) {
    if (clientEditMode) {
      throw new Error(e);
    } else {
      console.error(e);
    }
  }

  // if customContent contains 'base-course: 123' then load that course as the base content
  const baseCourseId = customContent?.['base-course'];

  if (baseCourseId) {
    console.log('[getContent] bespoke base-course override', {
      courseId,
      baseCourseId
    });
    const baseContent = await fetchJSON(baseCourseId);
    content = baseContent;
  }

  _customContent = customContent;

  console.log('[getContent] merging base + custom', {
    courseId,
    firm,
    audioDoc,
    baseResolved: !!content,
    baseTitle: content?.course?.title,
    basePages: content?.pages?.length,
    customResolved: !!customContent,
    customTitle: customContent?.course?.title,
    customHasPatches: !!customContent?.patches
  });

  const mergedContent = mergeCustomContent(content, customContent);

  // don't preload the images if we're in audio script mode
  if (!audioDoc && !wordDoc) {
    usePreloadImages(mergedContent, { justFirstScreen: true });
  }

  mergedContent.filename = customContent
    ? `${firm}_${courseId}`
    : `${courseId}`;

  return mergedContent;
}

export async function hotUpdateContent() {
  if (import.meta.hot) {
    const loadYaml = (await import('js-yaml')).load;
    const useContent = (await import('../stores/useContent')).default;

    if (typeof courseId !== 'number') {
      throw new Error('No valid course ID.');
    }

    const loadRaw = async (path: string) => {
      try {
        return (await fetch(path)).text();
      } catch (e) {
        return '';
      }
    };

    const raw = await Promise.all([
      loadRaw(`/src/content/${courseId}.yml`),
      loadRaw(`/src/customizations/${firm}/${firm}_${courseId}.yml`)
    ]);

    const objs = raw.map(s => loadYaml(s)) as [Content, Content];
    const content = mergeCustomContent(...objs);

    useContent().load({ content });
  }
}

function checkForDupIds(content: Content) {
  let dups: string[] = [];
  try {
    const m = JSON.stringify(content?.pages).matchAll(/"id":"([^"]+)"/g);
    const ids = [...m].map(s => s[1]);

    // loop over IDs and check invalid characters
    ids.forEach(id => {
      if (id.match(/[^a-zA-Z0-9-_]/g))
        bugAlert(`Invalid characters in ID: ${JSON.stringify(id)}`);
      if (id.match(/^[0-9]/g))
        bugAlert(`IDs cannot start with a number: ${JSON.stringify(id)}`);
    });

    dups =
      ids?.filter((id, i, a) => a.indexOf(id) !== i).map(s => s.slice(6, -1)) ||
      [];
  } catch (e) {
    console.error(e);
  }

  if (dups.length > 0) {
    const error = `
Duplicated IDs: 

${dups.map(id => `  #${id}`).join('\n')}

Please search your course YAML files for these IDs and make sure they are unique.
`;

    bugAlert(error);
  }
}

if (import.meta.hot) {
  import.meta.hot.accept();
  import.meta.hot.on('vite:beforeUpdate', hotUpdateContent);
}
